import { Color3, Matrix, Mesh, NullEngine, Scene, TransformNode, Vector3, VertexData, type Material } from "@babylonjs/core";
import { LoadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader.js";
import "@babylonjs/loaders/glTF/2.0/index.js";
import { SHIP_PROFILES, SURFACE_PROFILES, slotColor, surfaceForSlot, type Materials, type SurfaceFamily } from "./materials.ts";

/**
 * Game models authored as Blender scripts (tools/models) and shipped as .glb.
 *
 * Each file is flattened once into plain geometry in the model's own frame
 * (Babylon axes: +x right, +y up, +z forward): meshes under an Empty named
 * `pivot:<part>` belong to that animated part, everything else to the static
 * root part, and geometry is kept per (part, material slot). Material *names*
 * in the file are slot names: palette keys, or slots coloured per instance
 * (crew, coat). Instances bake slot colours into vertex colours and batch by
 * semantic surface family, preserving cloth, timber and metal response. An
 * authored COLOR_0 (character presets: albedo detail and baked occlusion)
 * multiplies the slot colour.
 */
export const SCENERY_MODEL_NAMES = [
  "palm", "palmLeaning", "pine", "marshTree", "lighthouse", "windmill", "hut", "hutFrost",
  "arch", "bigRock", "shipwreck", "obsidianSpire", "iceSpire", "deadTree", "stiltHut",
  "pillars", "statue", "supplyBarrel", "questBoard", "merchantStall", "tavern", "shop",
  "pierPile", "pierLantern", "buoy",
  "beaconTower", "seaLookout", "stockadeGate", "palisade", "stakes", "stakesWall",
  "scaffoldTower", "fortShack", "crateStack", "bannerPole", "campfire",
  "gateLever", "lootCrate", "lootCrateBroken", "mountedTelescope",
] as const;
export type SceneryName = (typeof SCENERY_MODEL_NAMES)[number];
export const MODEL_NAMES = ["ship", "cannon", "crewmate", "crewmate_custom", "merchant", "chest", "shovel", "hands", "sword", "spyglass", "pistol", "hammer", "plank", "bucket", ...SCENERY_MODEL_NAMES] as const;
export type ModelName = (typeof MODEL_NAMES)[number];

interface Geometry {
  slot: string;
  /** `category=choice` for an interchangeable part (mesh named `opt:<category>=<choice>/…`), else null. */
  variant: string | null;
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  /** Linear RGBA per vertex from COLOR_0, multiplied into the slot colour. */
  colors: Float32Array | null;
}

interface PartData {
  /** "" for the static root part. */
  name: string;
  /** Name of the enclosing part ("" = root). */
  parent: string;
  /** Pivot relative to the parent part's pivot. */
  offset: Vector3;
  geometry: Geometry[];
}

type ModelData = PartData[];

export interface ModelInstance {
  readonly root: TransformNode;
  readonly meshes: Mesh[];
  /** Pivot node of an animated part; throws for unknown names so typos fail loudly. */
  part(name: string): TransformNode;
  /** Optional pivot (preset-only parts such as `eyelids`); null when the model lacks it. */
  findPart(name: string): TransformNode | null;
}

// glTF is right-handed with +z forward; the loader's handedness conversion
// leaves authored models facing -z, so turn them around once at load.
const FLIP = Matrix.RotationY(Math.PI);

const data = new Map<ModelName, ModelData>();
const pending = new Map<ModelName, Promise<void>>();

/**
 * Fetches and flattens the named models, once per page (a failed load may be
 * retried). Parsing runs on a GPU-free NullEngine: only CPU-side vertex data
 * is extracted, so loading never competes with rendering. The geometry is
 * scene-independent and shared by every game scene.
 */
export function loadModels(names: readonly ModelName[] = MODEL_NAMES): Promise<void> {
  const missing = names.filter((name) => !pending.has(name));
  if (missing.length > 0) {
    const batch = (async () => {
      const engine = new NullEngine();
      const scratch = new Scene(engine);
      try {
        await Promise.all(
          missing.map(async (name) => {
            const res = await fetch(new URL(`models/${name}.glb`, document.baseURI));
            if (!res.ok) throw new Error(`model ${name}: HTTP ${res.status}`);
            data.set(name, await parse(name, new Uint8Array(await res.arrayBuffer()), scratch));
          }),
        );
      } finally {
        engine.dispose();
      }
    })();
    for (const name of missing) {
      pending.set(name, batch.catch((e: unknown) => {
        pending.delete(name);
        throw e;
      }));
    }
  }
  return Promise.all(names.map((name) => pending.get(name)!)).then(() => undefined);
}

async function parse(model: ModelName, bytes: Uint8Array, scene: Scene): Promise<ModelData> {
  const container = await LoadAssetContainerAsync(bytes, scene, { pluginExtension: ".glb" });
  try {
    const pivotOf = (node: TransformNode | null): TransformNode | null => {
      for (let n = node; n; n = n.parent as TransformNode | null) if (n.name.startsWith("pivot:")) return n;
      return null;
    };
    const parts = new Map<string, PartData & { abs: Vector3; slots: Map<string, { slot: string; variant: string | null; p: number[]; n: number[]; u: number[]; i: number[]; c: number[]; colored: boolean }> }>();
    const partFor = (pivot: TransformNode | null): string => {
      const name = pivot ? pivot.name.slice("pivot:".length) : "";
      if (parts.has(name)) return name;
      const abs = pivot ? Vector3.TransformCoordinates(pivot.computeWorldMatrix(true).getTranslation(), FLIP) : Vector3.Zero();
      const parent = pivot ? partFor(pivotOf(pivot.parent as TransformNode | null)) : "";
      const parentAbs = pivot ? parts.get(parent)!.abs : Vector3.Zero();
      parts.set(name, { name, parent, offset: abs.subtract(parentAbs), abs, geometry: [], slots: new Map() });
      return name;
    };
    partFor(null);
    for (const node of container.transformNodes) if (node.name.startsWith("pivot:")) partFor(node);

    const world = new Matrix();
    const v = new Vector3();
    for (const mesh of container.meshes) {
      if (!(mesh instanceof Mesh) || mesh.getTotalVertices() === 0) continue;
      const slot = mesh.material?.name;
      if (!slot) throw new Error(`model mesh ${mesh.name} has no material`);
      let family: SurfaceFamily;
      try { family = surfaceForSlot(slot); }
      catch (cause) { throw new Error(`model ${model}: unknown surface slot ${slot}`, { cause }); }
      const part = parts.get(partFor(pivotOf(mesh)))!;
      mesh.computeWorldMatrix(true).multiplyToRef(FLIP, world);
      world.multiplyToRef(Matrix.Translation(-part.abs.x, -part.abs.y, -part.abs.z), world);
      // Read-only: the exporter dedupes identical accessors, so buffers may be shared between meshes.
      const pos = mesh.getVerticesData("position")!;
      const nrm = mesh.getVerticesData("normal")!;
      const uv = mesh.getVerticesData("uv");
      if (SURFACE_PROFILES[family].texture && (!uv || uv.length !== pos.length / 3 * 2 || !uv.every(Number.isFinite))) {
        throw new Error(`model ${model}, slot ${slot}: missing or invalid metric UV0`);
      }
      const idx = mesh.getIndices()!;
      // Blender writes COLOR_0 as RGBA; accept RGB too. Absent colours read as white.
      const col = mesh.getVerticesData("color");
      const colorSize = mesh.getVertexBuffer("color")?.getSize() ?? 4;
      const variant = mesh.name.startsWith("opt:") ? mesh.name.slice(4, mesh.name.indexOf("/")) : null;
      const key = `${slot}|${variant ?? ""}`;
      let acc = part.slots.get(key);
      if (!acc) part.slots.set(key, (acc = { slot, variant, p: [], n: [], u: [], i: [], c: [], colored: false }));
      if (col) acc.colored = true;
      const base = acc.p.length / 3;
      for (let k = 0; k < pos.length; k += 3) {
        Vector3.TransformCoordinatesFromFloatsToRef(pos[k]!, pos[k + 1]!, pos[k + 2]!, world, v);
        acc.p.push(v.x, v.y, v.z);
        Vector3.TransformNormalFromFloatsToRef(nrm[k]!, nrm[k + 1]!, nrm[k + 2]!, world, v).normalize();
        acc.n.push(v.x, v.y, v.z);
        const j = k / 3 * 2;
        acc.u.push(uv?.[j] ?? 0, uv?.[j + 1] ?? 0);
        const ci = k / 3 * colorSize;
        if (col) acc.c.push(col[ci]!, col[ci + 1]!, col[ci + 2]!);
        else acc.c.push(1, 1, 1);
      }
      // glTF triangles are counter-clockwise; Babylon's front faces are clockwise
      // in an unmirrored frame. The loader's handedness root mirrors (det < 0),
      // which already reverses the winding, so only unmirrored bakes need a flip.
      const flip = world.determinant() > 0;
      for (let k = 0; k < idx.length; k += 3) {
        acc.i.push(idx[k]! + base);
        if (flip) acc.i.push(idx[k + 2]! + base, idx[k + 1]! + base);
        else acc.i.push(idx[k + 1]! + base, idx[k + 2]! + base);
      }
    }
    const out: ModelData = [];
    for (const p of parts.values()) {
      for (const acc of p.slots.values()) {
        p.geometry.push({ slot: acc.slot, variant: acc.variant, positions: new Float32Array(acc.p), normals: new Float32Array(acc.n), uvs: new Float32Array(acc.u), indices: new Uint32Array(acc.i), colors: acc.colored ? new Float32Array(acc.c) : null });
      }
      out.push({ name: p.name, parent: p.parent, offset: p.offset, geometry: p.geometry });
    }
    return out;
  } finally {
    container.dispose();
  }
}

export interface SpawnOptions {
  /** Per-instance colours (hex) for recolourable slots such as `crew` or `coat`, and character tint channels. */
  colors?: Readonly<Record<string, string>>;
  /** Slots drawn with their own material (emissive, transparent) instead of vertex colours. */
  materials?: Readonly<Record<string, Material>>;
  /** Opt-in surface skin; "ship" uses SHIP_PROFILES via `Materials.ship`. */
  surfaceSet?: "ship";
  /** One choice per category for models with interchangeable parts (required for them). */
  variants?: Readonly<Record<string, string>>;
}

/** Categories and their choices in a loaded model's interchangeable parts. */
export function modelVariants(model: ModelName): ReadonlyMap<string, readonly string[]> {
  const parts = data.get(model);
  if (!parts) throw new Error(`model ${model} is not loaded`);
  const out = new Map<string, string[]>();
  for (const p of parts) for (const g of p.geometry) {
    if (!g.variant) continue;
    const [category, choice] = g.variant.split("=") as [string, string];
    const choices = out.get(category) ?? [];
    if (!choices.includes(choice)) choices.push(choice);
    out.set(category, choices);
  }
  return out;
}

/** Per-scene template meshes (disabled), cloned per instance so geometry is shared. */
const templates = new WeakMap<Scene, Map<string, Mesh>>();

/**
 * Merges `geometry` into one mesh. With `colorOf`, each slot's colour, times
 * any authored COLOR_0, is baked into vertex colours so every palette slot of
 * a part shares one material.
 */
function buildMesh(scene: Scene, name: string, geometry: readonly Geometry[], repeat: number, colorOf?: (slot: string) => Color3): Mesh {
  let vertices = 0;
  let indexCount = 0;
  for (const g of geometry) {
    vertices += g.positions.length / 3;
    indexCount += g.indices.length;
  }
  const positions = new Float32Array(vertices * 3);
  const normals = new Float32Array(vertices * 3);
  const uvs = new Float32Array(vertices * 2);
  const colors = colorOf ? new Float32Array(vertices * 4) : null;
  const indices = new Uint32Array(indexCount);
  let v = 0;
  let i = 0;
  for (const g of geometry) {
    positions.set(g.positions, v * 3);
    normals.set(g.normals, v * 3);
    for (let k = 0; k < g.uvs.length; k++) uvs[v * 2 + k] = g.uvs[k]! / repeat;
    for (let k = 0; k < g.indices.length; k++) indices[i + k] = g.indices[k]! + v;
    const n = g.positions.length / 3;
    if (colors && colorOf) {
      const c = colorOf(g.slot);
      for (let k = 0; k < n; k++) {
        const offset = (v + k) * 4, authored = k * 3, own = g.colors;
        colors[offset] = c.r * (own ? own[authored]! : 1);
        colors[offset + 1] = c.g * (own ? own[authored + 1]! : 1);
        colors[offset + 2] = c.b * (own ? own[authored + 2]! : 1);
        colors[offset + 3] = 1;
      }
    }
    v += n;
    i += g.indices.length;
  }
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = positions;
  vd.normals = normals;
  vd.indices = indices;
  vd.uvs = uvs;
  if (colors) vd.colors = colors;
  vd.applyToMesh(mesh);
  mesh.isPickable = false;
  mesh.setEnabled(false);
  return mesh;
}

/**
 * Builds an instance under a fresh root after loadGameAssets resolves.
 * Each animated part has one mesh per surface family, plus isolated explicit
 * material overrides. Templates retain rest-pose metric UVs and tint identity.
 */
export function spawnModel(scene: Scene, mats: Materials, model: ModelName, opts: SpawnOptions = {}): ModelInstance {
  const parts = data.get(model);
  if (!parts) throw new Error(`model ${model} is not loaded`);
  let byKey = templates.get(scene);
  if (!byKey) templates.set(scene, (byKey = new Map()));
  const cache = byKey;
  const colors = opts.colors ?? {};
  const special = opts.materials ?? {};
  const set = opts.surfaceSet ?? "default";
  const profiles = opts.surfaceSet === "ship" ? SHIP_PROFILES : SURFACE_PROFILES;
  const variants = opts.variants;
  const chosen = (g: Geometry): boolean => {
    if (!g.variant) return true;
    if (!variants) throw new Error(`model ${model} has interchangeable parts: pass one variant per category`);
    const [category, choice] = g.variant.split("=");
    return variants[category!] === choice;
  };
  const tintOf = (slot: string): string => {
    const hex = slotColor(slot, colors);
    if (!hex) throw new Error(`model ${model}: no colour for slot ${slot}`);
    return hex;
  };
  const colorOf = (slot: string): Color3 => Color3.FromHexString(tintOf(slot)).toLinearSpace();

  const root = new TransformNode(model, scene);
  const nodes = new Map<string, TransformNode>([["", root]]);
  const meshes: Mesh[] = [];
  const add = (key: string, node: TransformNode, material: Material, build: () => Mesh): void => {
    let t = cache.get(key);
    if (!t) cache.set(key, (t = build()));
    const mesh = t.clone(t.name, node);
    mesh.material = material;
    mesh.setEnabled(true);
    meshes.push(mesh);
  };
  // Parts are stored parents-first (partFor recurses before inserting).
  for (const p of parts) {
    let node = nodes.get(p.name);
    if (!node) {
      node = new TransformNode(`${model}:${p.name}`, scene);
      node.parent = nodes.get(p.parent)!;
      node.position.copyFrom(p.offset);
      // Articulated hands are the default, including non-animated showroom instances.
      // The shared arm helper enables exactly one merged hand only for distant LOD.
      if (p.name === "handOpenL" || p.name === "handOpenR" || p.name === "handGripL" || p.name === "handGripR") node.setEnabled(false);
      nodes.set(p.name, node);
    }
    const label = `${model}:${p.name || "static"}`, setLabel = `${label}:${set}`;
    const groups = new Map<SurfaceFamily, Geometry[]>();
    for (const g of p.geometry) {
      if (!chosen(g)) continue;
      const family = surfaceForSlot(g.slot);
      const material = special[g.slot];
      if (material) {
        add(`${setLabel}:override:${g.slot}:${g.variant ?? ""}`, node, material, () => buildMesh(scene, `${label}:${g.slot}`, [g], profiles[family].metres));
      } else {
        const group = groups.get(family);
        if (group) group.push(g);
        else groups.set(family, [g]);
      }
    }
    for (const [family, geometry] of groups) {
      // Membership matters: removing an overridden slot must not reuse a full batch.
      const tintKey = geometry.map(g => `${g.slot}|${g.variant ?? ""}|${tintOf(g.slot)}`).sort();
      const key = `${setLabel}:${family}:${JSON.stringify(tintKey)}`;
      const material = opts.surfaceSet === "ship" ? mats.ship(family) : mats.vertexColored(family);
      add(key, node, material, () => buildMesh(scene, `${label}:${family}`, geometry, profiles[family].metres, colorOf));
    }
  }
  return {
    root,
    meshes,
    part(name) {
      const n = nodes.get(name);
      if (!n || name === "") throw new Error(`model ${model} has no part ${name}`);
      return n;
    },
    findPart(name) {
      return name === "" ? null : nodes.get(name) ?? null;
    },
  };
}
